// Tiny Supabase client: auth (GoTrue) + data (PostgREST). No SDK needed.
// The anon key is public by design; every row is protected by row-level security.

declare const __SUPABASE_URL__: string;
declare const __SUPABASE_ANON_KEY__: string;
declare const __APP_ENV__: string;
declare const __APP_VERSION__: string;
export const APP_VERSION = __APP_VERSION__;
declare const __TURNSTILE_SITE_KEY__: string;
/** Cloudflare Turnstile (bot check on sign-in / sign-up). Empty = off. */
export const TURNSTILE_SITE_KEY = __TURNSTILE_SITE_KEY__;

export const APP_ENV = __APP_ENV__;
const URL_BASE = __SUPABASE_URL__;
const ANON = __SUPABASE_ANON_KEY__;
const STORE = "rca.session";
/** When this sign-in started, and the last time anyone touched the app (shared by all tabs). */
export const SIGNED_IN = "rca.signed_in_at";
export const ACTIVE = "rca.active_at";
export const SIGNOUT_REASON = "rca.signout_reason";

export type Session = {
  access_token: string;
  refresh_token: string;
  expires_at: number; // epoch seconds
  user: { id: string; email: string };
};

export class ApiError extends Error {
  constructor(public status: number, message: string, public details?: unknown) {
    super(message);
  }
}

let session: Session | null = load();
const listeners = new Set<(s: Session | null) => void>();

function load(): Session | null {
  try {
    const raw = localStorage.getItem(STORE);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}
function save(s: Session | null) {
  const fresh = !!s && !session;
  session = s;
  try {
    if (s) localStorage.setItem(STORE, JSON.stringify(s));
    else localStorage.removeItem(STORE);
    if (fresh) { localStorage.setItem(SIGNED_IN, String(Date.now())); localStorage.setItem(ACTIVE, String(Date.now())); }
    if (!s) localStorage.removeItem(SIGNED_IN);
  } catch {
    /* private mode: keep in memory */
  }
  listeners.forEach((l) => l(s));
}
export function onSession(fn: (s: Session | null) => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export const getSession = () => session;

async function authFetch(path: string, body?: unknown, method = "POST", token?: string) {
  const res = await fetch(`${URL_BASE}/auth/v1/${path}`, {
    method,
    headers: {
      apikey: ANON,
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error_description || data.msg || data.message || "Sign-in failed";
    throw new ApiError(res.status, friendlyAuth(msg));
  }
  return data;
}

function friendlyAuth(msg: string) {
  if (/invalid totp|invalid mfa|code.*(invalid|expired)/i.test(msg)) return "That code didn't work. Check the 6 digits in your authenticator app and try again.";
  if (/invalid login/i.test(msg)) return "That email and password don't match. Try again or use a sign-in link.";
  if (/email not confirmed/i.test(msg)) return "Confirm your email first. Check your inbox for the link.";
  if (/signups not allowed|not allowed/i.test(msg)) return "This email hasn't been invited yet. Ask the owner to invite you.";
  return msg;
}

function fromToken(data: any): Session {
  return {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    expires_at: data.expires_at ?? Math.floor(Date.now() / 1000) + (data.expires_in ?? 3600),
    user: { id: data.user.id, email: data.user.email },
  };
}

const cap = (t?: string) => (t ? { gotrue_meta_security: { captcha_token: t } } : {});
export const auth = {
  async signInWithPassword(email: string, password: string, captcha?: string) {
    save(fromToken(await authFetch("token?grant_type=password", { email, password, ...cap(captcha) })));
  },
  /** First-time sign-up. Only invited emails get access (checked in the database). */
  async signUp(email: string, password: string, captcha?: string): Promise<"signed-in" | "confirm-email"> {
    const d = await authFetch(`signup?redirect_to=${encodeURIComponent(location.origin)}`, { email, password, ...cap(captcha) });
    if (d.access_token) {
      save(fromToken(d));
      return "signed-in";
    }
    return "confirm-email";
  },
  async sendLink(email: string, captcha?: string) {
    await authFetch(`otp?redirect_to=${encodeURIComponent(location.origin)}`, { email, create_user: false, ...cap(captcha) });
  },
  async sendReset(email: string, captcha?: string) {
    await authFetch(`recover?redirect_to=${encodeURIComponent(location.origin + "/set-password")}`, { email, ...cap(captcha) });
  },
  async setPassword(password: string) {
    const s = await ensureFresh();
    await authFetch("user", { password }, "PUT", s?.access_token);
  },
  async signOut() {
    const s = session;
    save(null);
    if (s) await authFetch("logout", undefined, "POST", s.access_token).catch(() => undefined);
  },
  /** Handle the #access_token=… fragment after clicking an email link. */
  async consumeRedirect(): Promise<boolean> {
    if (!location.hash.includes("access_token")) return false;
    const p = new URLSearchParams(location.hash.slice(1));
    const access_token = p.get("access_token")!;
    const user = await authFetch("user", undefined, "GET", access_token);
    save({
      access_token,
      refresh_token: p.get("refresh_token") || "",
      expires_at: Number(p.get("expires_at")) || Math.floor(Date.now() / 1000) + 3600,
      user: { id: user.id, email: user.email },
    });
    history.replaceState(null, "", location.pathname + location.search);
    return true;
  },
};

// ───────────────────────────── two-factor (TOTP) ─────────────────────────────
export type Factor = { id: string; factor_type: string; status: "verified" | "unverified"; friendly_name?: string; created_at: string };
/** Which assurance level the current token has: aal1 = password only, aal2 = password + code. */
export function currentAal(): "aal1" | "aal2" {
  try {
    const payload = JSON.parse(atob(session!.access_token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.aal === "aal2" ? "aal2" : "aal1";
  } catch { return "aal1"; }
}
export const mfa = {
  async factors(): Promise<Factor[]> {
    const s = await ensureFresh();
    const u = await authFetch("user", undefined, "GET", s?.access_token);
    return (u.factors ?? []).filter((f: Factor) => f.factor_type === "totp");
  },
  /** Start setting up an authenticator app. Returns the QR code (SVG data URL) and the manual key. */
  async enroll(): Promise<{ id: string; qr: string; secret: string }> {
    const s = await ensureFresh();
    // remove an abandoned, never-verified attempt first
    for (const f of await mfa.factors()) if (f.status === "unverified") await authFetch(`factors/${f.id}`, undefined, "DELETE", s?.access_token).catch(() => undefined);
    const d = await authFetch("factors", { factor_type: "totp", friendly_name: `Authenticator ${new Date().toISOString().slice(0, 10)}` }, "POST", s?.access_token);
    return { id: d.id, qr: d.totp.qr_code, secret: d.totp.secret };
  },
  /** Check a 6-digit code; on success the session is upgraded to aal2. */
  async verify(factorId: string, code: string) {
    const s = await ensureFresh();
    const ch = await authFetch(`factors/${factorId}/challenge`, {}, "POST", s?.access_token);
    const d = await authFetch(`factors/${factorId}/verify`, { challenge_id: ch.id, code: code.replace(/\s/g, "") }, "POST", s?.access_token);
    save(fromToken(d));
  },
  async remove(factorId: string) {
    const s = await ensureFresh();
    await authFetch(`factors/${factorId}`, undefined, "DELETE", s?.access_token);
    await ensureFresh();
  },
};

let refreshing: Promise<Session | null> | null = null;
async function ensureFresh(): Promise<Session | null> {
  if (!session) return null;
  if (session.expires_at - 60 > Date.now() / 1000) return session;
  refreshing ??= authFetch("token?grant_type=refresh_token", { refresh_token: session.refresh_token })
    .then((d) => {
      save(fromToken(d));
      return session;
    })
    .catch(() => {
      save(null);
      return null;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

// ───────────────────────────── data ─────────────────────────────
type Params = Record<string, string | number | boolean | undefined | null>;

async function rest(method: string, path: string, opts: { params?: Params; body?: unknown; prefer?: string; count?: boolean } = {}) {
  const s = await ensureFresh();
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(opts.params || {})) if (v !== undefined && v !== null) qs.append(k, String(v));
  const res = await fetch(`${URL_BASE}/rest/v1/${path}${qs.toString() ? "?" + qs : ""}`, {
    method,
    headers: {
      apikey: ANON,
      Authorization: `Bearer ${s?.access_token ?? ANON}`,
      "Content-Type": "application/json",
      Prefer: [opts.prefer, opts.count ? "count=exact" : ""].filter(Boolean).join(","),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 401) {
    save(null);
    throw new ApiError(401, "Your session ended. Sign in again.");
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, friendlyDb(data?.message || res.statusText), data);
  if (opts.count) {
    const total = Number(res.headers.get("content-range")?.split("/")[1] ?? 0);
    return { rows: data, total };
  }
  return data;
}

function friendlyDb(msg: string) {
  if (/row-level security/i.test(msg)) return "You don't have permission to change this.";
  if (/duplicate key/i.test(msg)) return "That already exists.";
  if (/violates foreign key/i.test(msg)) return "This is still linked to other records.";
  if (/statement timeout/i.test(msg)) return "That took too long to load. Please try again in a moment.";
  return msg;
}

export const db = {
  /** Reads every matching row. The server returns at most 1,000 rows per request, so
   *  without an explicit limit this pages through until everything is loaded. */
  async select<T = any>(table: string, params: Params = {}): Promise<T[]> {
    if (params.limit != null) return rest("GET", table, { params });
    const PAGE = 1000, out: T[] = [];
    for (let offset = 0; ; offset += PAGE) {
      const rows: T[] = await rest("GET", table, { params: { ...params, limit: PAGE, offset } });
      out.push(...rows);
      if (rows.length < PAGE || offset > 200_000) return out;
    }
  },
  async selectCount<T = any>(table: string, params: Params = {}): Promise<{ rows: T[]; total: number }> {
    return rest("GET", table, { params, count: true });
  },
  async one<T = any>(table: string, params: Params = {}): Promise<T | null> {
    const rows = await rest("GET", table, { params: { ...params, limit: 1 } });
    return rows?.[0] ?? null;
  },
  insert<T = any>(table: string, body: unknown, params: Params = {}): Promise<T[]> {
    return rest("POST", table, { body, params, prefer: "return=representation" });
  },
  upsert<T = any>(table: string, body: unknown, onConflict?: string): Promise<T[]> {
    return rest("POST", table, {
      body,
      params: onConflict ? { on_conflict: onConflict } : {},
      prefer: "return=representation,resolution=merge-duplicates",
    });
  },
  update<T = any>(table: string, match: Params, body: unknown): Promise<T[]> {
    return rest("PATCH", table, { body, params: match, prefer: "return=representation" });
  },
  remove(table: string, match: Params) {
    return rest("DELETE", table, { params: match });
  },
  rpc<T = any>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
    return rest("POST", `rpc/${fn}`, { body: args });
  },
};

/** PostgREST filter helpers */
export const eq = (v: string | number | boolean) => `eq.${v}`;
export const ilike = (v: string) => `ilike.*${v.replace(/[*,()]/g, " ")}*`;
export const inList = (vs: (string | number)[]) => `in.(${vs.map((v) => `"${v}"`).join(",")})`;

/** Supabase Storage: upload a file to a public bucket, returns its public URL. */
export async function uploadPublic(bucket: string, path: string, file: File): Promise<string> {
  const s = await ensureFresh();
  const res = await fetch(`${URL_BASE}/storage/v1/object/${bucket}/${path}`, {
    method: "POST",
    headers: { apikey: ANON, Authorization: `Bearer ${s?.access_token ?? ANON}`, "Content-Type": file.type, "x-upsert": "false" },
    body: file,
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new ApiError(res.status, /policy|row-level/i.test(d.message ?? "") ? "You don't have permission to upload pictures." : d.message || "Upload failed");
  }
  return `${URL_BASE}/storage/v1/object/public/${bucket}/${path}`;
}
